import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, pointerWithin, useDroppable, useSensor, useSensors,
  type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AiTermsGate from '../ai/AiTermsGate';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLearner } from '../learners/LearnerContext';
import { useSettings } from '../settings/SettingsContext';
import { useEntitlements } from '../subscription/offers';
import { prepareSpeech, speak, stopSpeaking } from '../games/speak';
import { customFor, loadMedia, playCustom, removeMedia, saveAudio, saveImage, termOf } from '../media/media';
import Pic from '../media/Pic';
import { type TalkWord, type WordType } from './vocabulary';
import TalkHelper, { type HelperChange } from './TalkHelper';
import { useAiAccess } from '../ai/useAiAccess';
import { useT } from '../i18n/i18n';
import ButtonForm, { NEW_PAGE, type ButtonValues } from './ButtonForm';
import {
  CORE_ID, MAX_PAGES, addOwn, addPage, allWords, containerOf, fromData, idsIn, isFull, isOwn, place, removePage, removeWord,
  reorder, reorderPages, setHidden, updateOwn, updatePage, wordOf, type Layout,
} from './layout';

const talkTerm = (word: string) => `talk:${termOf(word)}`;
const TAB = 'tab:';
const ZONE = 'zone:';

const collision: CollisionDetection = (args) => {
  const overTab = pointerWithin(args).filter((h) => String(h.id).startsWith(TAB));
  if (overTab.length && !String(args.active.id).startsWith(TAB)) return overTab;
  const hits = closestCenter(args);
  const items = hits.filter((h) => !String(h.id).startsWith(ZONE) && h.id !== args.active.id);
  return items.length ? items : hits;
};

function SortableCell({ id, editing, children }: { id: string; editing: boolean; children: (handle: ReactNode, dragging: boolean) => ReactNode }) {
  const t = useT();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editing });
  const { onKeyDown, ...pointer } = listeners ?? {};
  const handle = editing ? (
    <button type="button" ref={setActivatorNodeRef} className="tb-move" aria-label={t('Move')} {...attributes} onKeyDown={onKeyDown as ((e: KeyboardEvent<HTMLButtonElement>) => void) | undefined}>
      <span aria-hidden="true">⠿</span>
    </button>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      className={`tb-cell${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...(editing ? pointer : {})}
    >
      {children(handle, isDragging)}
    </div>
  );
}

function SortableTab({ id, editing, children }: { id: string; editing: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isOver, isDragging } = useSortable({ id: TAB + id, disabled: !editing });
  return (
    <div
      ref={setNodeRef}
      className={`tb-tab-wrap${isOver ? ' is-over' : ''}${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...(editing ? { ...attributes, ...listeners, role: undefined, tabIndex: undefined } : {})}
    >
      {children}
    </div>
  );
}

function Zone({ id, className, label, children }: { id: string; className: string; label: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: ZONE + id });
  return <div ref={setNodeRef} className={`${className}${isOver ? ' is-over' : ''}`} role="group" aria-label={label}>{children}</div>;
}

export default function TalkBoardPage() {
  const t = useT();
  const entitlements = useEntitlements();
  const { learnerId, learner } = useLearner();
  const { settings } = useSettings();
  const [layout, setLayout] = useState<Layout>(() => fromData(null));
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');
  const [sentence, setSentence] = useState<TalkWord[]>([]);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newPage, setNewPage] = useState<{ label: string; picture: string } | null>(null);
  const [pageDraft, setPageDraft] = useState<{ label: string; picture: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'page' | 'button'; id: string } | null>(null);
  const before = useRef<Layout | null>(null);
  const aiAccess = useAiAccess().access;
  const developer = !!aiAccess?.visible;
  const mediaAllowed = !!entitlements?.talkBoard;

  useEffect(() => {
    if (!entitlements?.talkBoard || !learnerId) return;
    let cancelled = false;
    setLoaded(false);
    void loadMedia(learnerId);
    api<unknown>(`/talk-board?learnerId=${encodeURIComponent(learnerId)}`, { token: true })
      .then((d) => { if (!cancelled) { const l = fromData(d); setLayout(l); setCategory(l.pages[0]?.id ?? ''); setLoaded(true); } })
      .catch((e) => { if (!cancelled) { setError(e instanceof Error ? e.message : t('Could not load the board')); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [entitlements?.talkBoard, learnerId]);

  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => {
    if (loaded) void prepareSpeech(allWords(layout).map((w) => w.word), settings.ttsVoice, settings.ttsRate);
  }, [loaded, layout, settings.ttsVoice, settings.ttsRate]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const hidden = new Set(layout.hidden);
  const page = layout.pages.find((p) => p.id === category) ?? layout.pages[0];
  const pageWords = page ? page.words : [];

  async function save(next: Layout) {
    setLayout(next);
    setError(null);
    try {
      await api(`/talk-board?learnerId=${encodeURIComponent(learnerId ?? '')}`, { method: 'PUT', token: true, body: next });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save the board'));
    }
  }

  const say = (text: string) => void speak(text, { voiceName: settings.ttsVoice, rate: settings.ttsRate, fromTalkBoard: true });
  const sayWord = (word: string) => playCustom(talkTerm(word)) ?? speak(word, { voiceName: settings.ttsVoice, rate: settings.ttsRate, fromTalkBoard: true });
  async function sayAll(list: TalkWord[]) {
    if (!list.some((s) => customFor(talkTerm(s.word))?.audio)) { say(list.map((s) => s.word).join(' ')); return; }
    for (const s of list) await sayWord(s.word);
  }

  function tap(word: TalkWord) {
    if (editing) { setSelected(word.id); return; }
    setSentence((s) => [...s, word].slice(-12));
    stopSpeaking();
    void sayWord(word.word);
  }

  async function applyMedia(word: string, v: ButtonValues, oldWord?: string) {
    if (!mediaAllowed) return;
    const term = talkTerm(word);
    if (oldWord && termOf(oldWord) !== termOf(word)) {
      const old = customFor(talkTerm(oldWord));
      if (old?.image && !v.photo.blob && !v.photo.remove) v.photo = { blob: await (await fetch(old.image)).blob() };
      if (old?.audio && !v.voice.blob && !v.voice.remove) v.voice = { blob: await (await fetch(old.audio)).blob() };
    }
    if (v.photo.blob) await saveImage(term, v.photo.blob, learnerId);
    else if (v.photo.remove && customFor(term)?.image) await removeMedia('image', term, learnerId);
    if (v.voice.blob) await saveAudio(term, v.voice.blob, learnerId);
    else if (v.voice.remove && customFor(term)?.audio) await removeMedia('audio', term, learnerId);
  }

  function resolveLocation(next: Layout, v: ButtonValues): { layout: Layout; to: string } {
    if (v.location !== NEW_PAGE || !v.newPage) return { layout: next, to: v.location };
    const made = addPage(next, v.newPage.label.slice(0, 30), v.newPage.picture.slice(0, 8) || '📁');
    return { layout: made.layout, to: made.id };
  }

  async function add(v: ButtonValues) {
    setBusy(true);
    try {
      const { layout: base, to } = resolveLocation(layout, v);
      if (isFull(base, to)) { setError(t('That page is full. A page holds up to 60 buttons.')); return; }
      const word = { id: `own:${Date.now()}`, word: v.word.slice(0, 40), picture: v.picture.slice(0, 8) || '⭐', type: v.type };
      await save(addOwn(base, word, to));
      if (to !== CORE_ID) setCategory(to);
      await applyMedia(word.word, v);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save the board'));
    } finally {
      setBusy(false);
    }
  }

  async function edit(id: string, v: ButtonValues) {
    const current = wordOf(layout, id);
    if (!current) return;
    setBusy(true);
    try {
      let next = layout;
      let targetId = id;
      const changed = v.word !== current.word || (v.picture || '⭐') !== current.picture || v.type !== current.type;
      if (changed && !isOwn(id)) {
        const from = containerOf(next, id) ?? CORE_ID;
        const index = idsIn(next, from).indexOf(id);
        targetId = `own:${Date.now()}`;
        next = removeWord(next, id);
        next = { ...next, own: [...next.own, { id: targetId, word: v.word.slice(0, 40), picture: v.picture.slice(0, 8) || '⭐', type: v.type }] };
        next = place(next, targetId, from, index);
      } else if (changed) {
        next = updateOwn(next, id, { word: v.word.slice(0, 40), picture: v.picture.slice(0, 8) || '⭐', type: v.type });
      }
      if (containerOf(next, targetId) !== v.location) {
        if (isFull(next, v.location)) { setError(t('That page is full. A page holds up to 60 buttons.')); return; }
        next = place(next, targetId, v.location);
      }
      next = setHidden(next, targetId, v.hidden);
      await save(next);
      await applyMedia(v.word, v, current.word);
      setSelected(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save the board'));
    } finally {
      setBusy(false);
    }
  }

  function applyChanges(changes: HelperChange[]) {
    let next = layout;
    changes.forEach((c, i) => {
      if (c.op === 'add' && c.page && c.word && c.type) {
        const to = c.page === CORE_ID || next.pages.some((p) => p.id === c.page) ? c.page : (next.pages[0]?.id ?? CORE_ID);
        if (!isFull(next, to)) next = addOwn(next, { id: `own:${Date.now()}-${i}`, word: c.word, picture: c.picture || '⭐', type: c.type as WordType }, to);
      } else if (c.op === 'hide' && c.id) next = setHidden(next, c.id, true);
      else if (c.op === 'show' && c.id) next = setHidden(next, c.id, false);
      else if (c.op === 'remove' && c.id) next = removeWord(next, c.id);
    });
    void save(next);
  }

  function containerFor(overId: string): string | null {
    if (overId.startsWith(ZONE)) return overId.slice(ZONE.length);
    if (overId.startsWith(TAB)) return null;
    return containerOf(layout, overId);
  }

  function onDragStart(e: DragStartEvent) {
    before.current = layout;
    setSelected(null);
    if (!String(e.active.id).startsWith(TAB)) stopSpeaking();
  }

  function onDragOver(e: DragOverEvent) {
    const active = String(e.active.id);
    const over = e.over ? String(e.over.id) : null;
    if (!over || active.startsWith(TAB)) return;
    const from = containerOf(layout, active);
    const to = containerFor(over);
    if (!from || !to || from === to || isFull(layout, to)) return;
    const index = over.startsWith(ZONE) ? undefined : idsIn(layout, to).indexOf(over);
    setLayout(place(layout, active, to, index));
  }

  function onDragEnd(e: DragEndEvent) {
    const active = String(e.active.id);
    const over = e.over ? String(e.over.id) : null;
    const start = before.current;
    before.current = null;
    if (!over) { if (start) setLayout(start); return; }
    if (active.startsWith(TAB)) {
      if (!over.startsWith(TAB)) return;
      const from = layout.pages.findIndex((p) => TAB + p.id === active);
      const to = layout.pages.findIndex((p) => TAB + p.id === over);
      if (from >= 0 && to >= 0 && from !== to) void save(reorderPages(layout, from, to));
      return;
    }
    if (over.startsWith(TAB)) {
      const target = over.slice(TAB.length);
      if (containerOf(layout, active) !== target && !isFull(layout, target)) {
        void save(place(layout, active, target));
        setCategory(target);
      } else if (start) setLayout(start);
      return;
    }
    const container = containerOf(layout, active);
    if (!container) return;
    const ids = idsIn(layout, container);
    const from = ids.indexOf(active);
    const to = over.startsWith(ZONE) ? ids.length - 1 : ids.indexOf(over);
    const next = from >= 0 && to >= 0 && from !== to ? reorder(layout, container, from, to) : layout;
    if (start && JSON.stringify(start) !== JSON.stringify(next)) void save(next);
    else setLayout(next);
  }

  function makePage() {
    if (!newPage?.label.trim()) return;
    const made = addPage(layout, newPage.label.trim().slice(0, 30), newPage.picture.trim().slice(0, 8) || '📁');
    void save(made.layout);
    setCategory(made.id);
    setNewPage(null);
  }

  if (entitlements === null) return <p className="muted">{t('Loading…')}</p>;
  if (!entitlements.talkBoard) {
    return (
      <div className="card">
        <h2>{t('Talk Board')}</h2>
        <p>{t('A picture board the learner taps to speak — single words or whole sentences, with the same buttons always in the same place.')}</p>
        <p className="field-hint">{t('Talk Board is an add-on, charged per learner. It can be bought on its own or with a plan.')}</p>
        <Link className="btn" to="/account/subscription">{t('See Talk Board')}</Link>
      </div>
    );
  }

  const cell = (id: string) => {
    const word = wordOf(layout, id);
    if (!word) return null;
    const isHidden = hidden.has(id);
    if (isHidden && !editing) return <div key={id} className="tb-cell is-gap" aria-hidden="true" />;
    return (
      <SortableCell key={id} id={id} editing={editing}>
        {(handle) => (
          <>
            <button
              type="button"
              className={`tb-btn tb-${word.type}${isHidden ? ' is-hidden' : ''}${selected === id ? ' is-selected' : ''}`}
              onClick={() => tap(word)}
              aria-label={editing ? t('Change {word}', { word: word.word }) : word.word}
            >
              <Pic text={talkTerm(word.word)} fallback={<span className="tb-pic" aria-hidden="true">{word.picture}</span>} className="tb-photo" />
              <span className="tb-word">{word.word}</span>
              {editing && isHidden && <span className="tb-state">{t('Hidden')}</span>}
            </button>
            {handle}
          </>
        )}
      </SortableCell>
    );
  };

  const selectedWord = selected ? wordOf(layout, selected) : undefined;
  const editor = selectedWord && selected && (
    <div className="card tb-add" key={`edit-${selected}`}>
      <h3>{t('Change “{word}”', { word: selectedWord.word })}</h3>
      <ButtonForm
        mode="edit"
        layout={layout}
        initial={{ word: selectedWord.word, picture: selectedWord.picture, type: selectedWord.type, location: containerOf(layout, selected) ?? CORE_ID, hidden: hidden.has(selected) }}
        term={talkTerm(selectedWord.word)}
        mediaAllowed={mediaAllowed}
        wordEditable
        busy={busy}
        onSubmit={(v) => void edit(selected, v)}
        onCancel={() => setSelected(null)}
        onRemove={() => setConfirm({ kind: 'button', id: selected })}
      />
    </div>
  );

  return (
    <div className="tb">
      <div className="tb-strip" aria-live="polite">
        <div className="tb-sentence" aria-label="Sentence">
          {sentence.length === 0
            ? <span className="tb-placeholder">Tap the pictures to talk</span>
            : sentence.map((s, i) => (
              <span key={i} className={`tb-chip tb-${s.type}`}>
                <Pic text={talkTerm(s.word)} fallback={<span aria-hidden="true">{s.picture}</span>} className="tb-chip-photo" /> {s.word}
              </span>
            ))}
        </div>
        <div className="tb-strip-actions">
          <button type="button" className="btn tb-speak" disabled={!sentence.length} onClick={() => { stopSpeaking(); void sayAll(sentence); }}>
            <span aria-hidden="true">🔊</span> Say it
          </button>
          <button type="button" className="btn secondary" disabled={!sentence.length} onClick={() => setSentence((s) => s.slice(0, -1))} aria-label="Remove the last word">⌫</button>
          <button type="button" className="btn secondary" disabled={!sentence.length} onClick={() => setSentence([])}>Clear</button>
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {editing && <p className="field-hint tb-edit-hint">{t('Drag a button to move it: within the row, between the main row and a page, or onto a page tab. Tap a button to change it.')}</p>}

      <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => { if (before.current) setLayout(before.current); before.current = null; }}>
        <SortableContext items={layout.core} strategy={rectSortingStrategy}>
          <Zone id={CORE_ID} className="tb-core" label={t('Main row')}>
            {layout.core.map(cell)}
          </Zone>
        </SortableContext>

        <div className="tb-tabs" role="tablist" aria-label="Pages">
          <SortableContext items={layout.pages.map((p) => TAB + p.id)} strategy={horizontalListSortingStrategy}>
            {layout.pages.map((p) => (
              <SortableTab key={p.id} id={p.id} editing={editing}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={page?.id === p.id}
                  className={`tb-tab${page?.id === p.id ? ' is-active' : ''}`}
                  onClick={() => { setCategory(p.id); setPageDraft(null); }}
                >
                  <span aria-hidden="true">{p.picture}</span> {p.label}
                </button>
              </SortableTab>
            ))}
          </SortableContext>
          {editing && layout.pages.length < MAX_PAGES && (
            <button type="button" className="tb-tab tb-tab-add" aria-label={t('Add a page')} onClick={() => setNewPage({ label: '', picture: '' })}>
              <span aria-hidden="true">＋</span> {t('Page')}
            </button>
          )}
        </div>

        {editing && newPage && (
          <form className="card tb-add tb-pageform" onSubmit={(e) => { e.preventDefault(); makePage(); }}>
            <div className="field">
              <label htmlFor="tb-newpage">{t('New page name')}</label>
              <div className="tb-inline-field">
                <input id="tb-newpage" value={newPage.label} maxLength={30} autoFocus onChange={(e) => setNewPage({ ...newPage, label: e.target.value })} />
                <input aria-label={t('Page emoji')} className="tb-emoji-input" value={newPage.picture} maxLength={8} placeholder="📁" onChange={(e) => setNewPage({ ...newPage, picture: e.target.value })} />
              </div>
            </div>
            <div className="toolbar">
              <button type="submit" className="btn" disabled={!newPage.label.trim()}>{t('Add page')}</button>
              <button type="button" className="btn ghost" onClick={() => setNewPage(null)}>{t('Cancel')}</button>
            </div>
          </form>
        )}

        {page && (
          <SortableContext items={pageWords} strategy={rectSortingStrategy}>
            <Zone id={page.id} className="tb-grid" label={page.label}>
              {!loaded ? <p className="muted">Loading…</p> : pageWords.map(cell)}
              {loaded && editing && pageWords.length === 0 && <p className="muted tb-empty">{t('This page is empty. Add a button below, or drag one onto it.')}</p>}
            </Zone>
          </SortableContext>
        )}
      </DndContext>

      <div className="tb-foot">
        <span className="field-hint">{learner ? t("{name}'s board", { name: learner.name }) : ''}</span>
        <button type="button" className={`btn ${editing ? '' : 'secondary'}`} onClick={() => { setEditing((e) => !e); setSelected(null); setNewPage(null); setPageDraft(null); }}>
          {editing ? t('Done editing') : t('Edit board')}
        </button>
      </div>

      {editing && editor}

      {editing && page && (
        <div className="card tb-add">
          <h3>{t('This page: {name}', { name: page.label })}</h3>
          {pageDraft ? (
            <form onSubmit={(e) => { e.preventDefault(); if (pageDraft.label.trim()) { void save(updatePage(layout, page.id, { label: pageDraft.label.trim().slice(0, 30), picture: pageDraft.picture.trim().slice(0, 8) || page.picture })); setPageDraft(null); } }}>
              <div className="field">
                <label htmlFor="tb-pagename">{t('Page name')}</label>
                <div className="tb-inline-field">
                  <input id="tb-pagename" value={pageDraft.label} maxLength={30} onChange={(e) => setPageDraft({ ...pageDraft, label: e.target.value })} />
                  <input aria-label={t('Page emoji')} className="tb-emoji-input" value={pageDraft.picture} maxLength={8} onChange={(e) => setPageDraft({ ...pageDraft, picture: e.target.value })} />
                </div>
              </div>
              <div className="toolbar">
                <button type="submit" className="btn" disabled={!pageDraft.label.trim()}>{t('Save page')}</button>
                <button type="button" className="btn ghost" onClick={() => setPageDraft(null)}>{t('Cancel')}</button>
              </div>
            </form>
          ) : (
            <div className="toolbar">
              <button type="button" className="btn secondary" onClick={() => setPageDraft({ label: page.label, picture: page.picture })}>{t('Rename page')}</button>
              <button type="button" className="btn quiet-danger" onClick={() => setConfirm({ kind: 'page', id: page.id })}>{t('Delete page')}</button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="card tb-add">
          <h3>{t('Add a button')}</h3>
          <ButtonForm
            mode="add"
            layout={layout}
            initial={{ word: '', picture: '', type: 'thing', location: page?.id ?? CORE_ID, hidden: false }}
            term={null}
            mediaAllowed={mediaAllowed}
            wordEditable
            busy={busy}
            onSubmit={(v) => void add(v)}
          />
        </div>
      )}

      {editing && developer && aiAccess && !aiAccess.termsAccepted && <AiTermsGate access={aiAccess} />}
      {editing && developer && aiAccess?.termsAccepted && (
        <TalkHelper
          core={layout.core.map((id) => wordOf(layout, id)).filter((w): w is TalkWord => !!w).map((w) => ({ id: w.id, word: w.word, type: w.type, hidden: hidden.has(w.id), own: isOwn(w.id) }))}
          pages={layout.pages.map((p) => ({
            id: p.id,
            label: p.label,
            words: p.words.map((id) => wordOf(layout, id)).filter((w): w is TalkWord => !!w).map((w) => ({ id: w.id, word: w.word, type: w.type, hidden: hidden.has(w.id), own: isOwn(w.id) })),
          }))}
          onApply={applyChanges}
        />
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'page' ? t('Delete this page?') : t('Remove this button?')}
        message={confirm?.kind === 'page'
          ? t('The page and every button on it are removed from this board. Your photos and recordings are kept.')
          : t('The button is removed from this board. Your photo and recording for it are kept.')}
        confirmLabel={confirm?.kind === 'page' ? t('Delete page') : t('Remove button')}
        destructive
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (!c) return;
          if (c.kind === 'page') {
            const next = removePage(layout, c.id);
            void save(next);
            setCategory(next.pages[0]?.id ?? '');
          } else {
            void save(removeWord(layout, c.id));
            setSelected(null);
          }
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
